<template>
  <master-layout
    :pageTitle="isEditing ? 'Edit My Application' : 'My Application'"
  >
    <div>
      <div class="chat-toolbar">
        <div class="toolbar-start">
          <VIcon
            @click="() => $router.go(-1)"
            :name="arrowBackOutline"
            :size="-4"
            class="backBttn cursorPointer"
          ></VIcon>
          <h2 class="vtext-page-title--regular ion-no-margin">
            {{ isEditing ? "Edit My application" : "My application" }}
          </h2>
        </div>
        <div v-if="isEditing" class="toolbar-end">
          <VButton class="vvd-btn-cancel" appearance="filled" label="Cancel" :disabled="loading" @click="closeEditMode" />
          <VButton class="vvd-btn-primary" connotation="cta" appearance="filled" label="Save" :disabled="loading" @click="updateAppDetails" />
        </div>
      </div>
      <div class="gridMargin">
        <div class="bgColor card-wrapper">
          <div v-if="loading" class="card-spinner-container">
            <VProgressRing connotation="cta" />
          </div>
          <div v-else>
            <div class="heading-wrapper heading-border">
              <div class="titleTooltip">
                <span class="customer-info-title">Basic Details</span>
                <span class="status-chip" :style="getChipStyle(appDetails.status)">
                  {{
                    appDetails.status.charAt(0).toUpperCase() +
                    appDetails.status.slice(1)
                  }}
                </span>
              </div>
              <ActionPopOver :style="{ '--width': '215px' }">
                <div class="action-list">
                  <div
                    :class="['action-item', { disabled: appDetails.status === 'revoked' }]"
                    @click="appDetails.status !== 'revoked' && openEditMode()"
                  >
                    <VIcon :name="createOutline"></VIcon>
                    Edit
                  </div>
                  <div
                    :class="['action-item', { disabled: appDetails.status === 'revoked' }]"
                    @click="appDetails.status !== 'revoked' && openDeleteAppModal()"
                  >
                    <VIcon :name="trashOutline" connotation="alert"></VIcon>
                    Delete
                  </div>
                  <div class="action-item" @click="openRevokeAppModal">
                    <VIcon :name="reloadOutline"></VIcon>
                    {{
                      appDetails.status?.toLowerCase() === "active"
                        ? "Revoke"
                        : "Activate"
                    }}
                  </div>
                </div>
              </ActionPopOver>
            </div>
            <div class="sectionPadding">
              <div class="input-row">
                <div class="flex100P">
                  <p class="ion-no-margin paddingBottom">App Name</p>
                  <div class="counter-wrapper">
                    <VTextField
                      v-model="appDetails.app_name"
                      placeholder="App Name"
                      :maxlength="30"
                      :disabled="!isEditing"
                      appearance="ghost"
                      class="vivid-input"
                      @change="appNameError = ''"
                    />
                    <div class="custom-counter">{{ (appDetails.app_name || '').length }}/30</div>
                  </div>
                  <span v-if="appNameError" class="error-text">{{ appNameError }}</span>
                </div>
              </div>

              <div class="input-row">
                <div class="flex100P w-100">
                  <p class="ion-no-margin paddingBottom">Description</p>
                  <div class="counter-wrapper">
                    <VTextArea
                      v-model="appDetails.description"
                      placeholder="Application Description"
                      :maxlength="100"
                      :rows="4"
                      :disabled="!isEditing"
                      appearance="ghost"
                      class="vivid-textarea"
                      @change="appDescriptionError = ''"
                    />
                    <div class="custom-counter">{{ (appDetails.description || '').length }}/100</div>
                  </div>
                </div>
              </div>

              <div class="input-row">
                <div class="flex100P">
                  <p class="ion-no-margin paddingBottom">Application ID</p>
                  <VTextField
                    :modelValue="appDetails.app_id"
                    :readonly="true"
                    aria-label="Application ID"
                    placeholder="Application ID"
                    appearance="ghost"
                    class="vivid-input vivid-input--readonly"
                  ></VTextField>
                </div>
                <div class="flex100P">
                  <p class="ion-no-margin paddingBottom">Date Created</p>
                  <VTextField
                    :modelValue="dateFormatter(appDetails.date_created)"
                    :readonly="true"
                    aria-label="Date Created"
                    placeholder="Date Created"
                    appearance="ghost"
                    class="vivid-input vivid-input--readonly"
                  ></VTextField>
                </div>
                <div class="flex100P">
                  <p class="ion-no-margin paddingBottom">Created By</p>
                  <VTextField
                    :modelValue="appDetails.created_by"
                    :readonly="true"
                    aria-label="Created By"
                    placeholder="Created By"
                    appearance="ghost"
                    class="vivid-input vivid-input--readonly"
                  ></VTextField>
                </div>
              </div>
            </div>
          </div>
        </div>
        <CapabilitiesCard
          :products="products"
          :featureLoading="featureLoading"
          :isEditing="isEditing"
          :capabilitiesError="capabilitiesError"
          @updateCapabilities="handleCapabilityToggle"
        />

        <AppApikeysCard
          :keysLoading="keysLoading"
          :clientColumns="clientColumns"
          :clientData="clientData"
          :appDetails="appDetails"
          :dateFormatter="dateFormatter"
          :getChipStyle="getChipStyle"
          @addApiKey="addApiKey"
          @copy="copyToClipboard"
          @openTokenModal="openAccessTokenModal"
          @updateKeyStatus="openRevokeApiKeyModal"
          @deleteKey="openDeleteKeyModal"
          @fetchClientSecretById="fetchClientSecretById"
          :loadingKeyId="loadingKeyId"
        />
      </div>
      <CustomWarningModal
        :isOpen="showDeleteAppModal"
        heading="Are you sure you want to delete this app?"
        description="Once deleted, all API keys, secrets, and linked configurations will be lost. Any connected integrations will stop working."
        cancelText="Cancel"
        confirmText="Delete"
        @cancel="closeDeleteAppModal"
        @confirm="confirmDeleteApp"
      />
      <CustomWarningModal
        :isOpen="showDeleteKeyModal"
        heading="Are you sure you want to delete this API key?"
        cancelText="Cancel"
        confirmText="Delete"
        @cancel="closeDeleteKeyModal"
        @confirm="confirmDeleteApiKey"
      />
      <CreateAccessTokenModal
        :keyId="keyToRefresh"
        :mode="tokenGenerationMode"
        :isOpen="showTokenModal"
        @onCancel="closeAccessTokenModal"
        @fetchList="fetchApiKeys"
      />
      <CreateAccessTokenModal
        mode="client_secret"
        :isOpen="showClientSecret"
        :clientSecret="clientSecrent"
        @onCancel="closeClientSecretModal"
      />
      <CustomWarningModal
        :isOpen="showRevokeAppModal"
        :heading="
          appDetails?.status === 'active'
            ? 'Are you sure you want to revoke this app?'
            : 'Are you sure you want to activate this app?'
        "
        :description="
          appDetails?.status === 'active'
            ? 'Once revoked, all API keys, secrets, and linked configurations will be disabled. Any connected integrations will stop working.'
            : 'Activating this app will restore its functionality and allow integrations to work again.'
        "
        cancelText="Cancel"
        :confirmText="appDetails?.status === 'active' ? 'Revoke' : 'Activate'"
        @cancel="closeRevokeAppModal"
        @confirm="updateAppStatus"
      />
      <CustomWarningModal
        :isOpen="showRevokeApiKeyModal"
        :heading="
          selectedApiKeyData?.status?.toLowerCase() === 'active'
            ? 'Are you sure you want to revoke this API key?'
            : 'Are you sure you want to activate this API key?'
        "
        :description="
          selectedApiKeyData?.status?.toLowerCase() === 'active'
            ? 'Revoking the key may take 3–5 minutes for the changes to take effect.'
            : 'Activating this key will restore its functionality and allow integrations to work again.'
        "
        cancelText="Cancel"
        :confirmText="
          selectedApiKeyData?.status?.toLowerCase() === 'active'
            ? 'Revoke'
            : 'Activate'
        "
        @cancel="closeRevokeApiKeyModal"
        @confirm="updateApiKeyStatus"
      />
    </div>
  </master-layout>
</template>

<script>
import ActionPopOver from "@/components/ActionPopOver.vue";
import CustomWarningModal from "@/components/CustomWarningModal.vue";
import apiInterceptor from "@/shared/apiInterceptor";
import { deleteAppHelper, updateAppStatusHelper } from "@/helpers/apps.helper";
import CreateAccessTokenModal from "./create-accessToken-modal.vue";
import FeatureCard from "./FeatureCard.vue";
import { getChipStyle, dateFormatter } from "@/helpers/global.helpers";
import CapabilitiesCard from "./app-capability-card.vue";
import AppApikeysCard from "./app-apikeys-card.vue";
import { useUIStore } from '@/stores/ui';
import { environment } from '@/shared/env.js';
import { useClipboard } from '@/composables/useClipboard';
export default {
  name: "ViewAppDetails",
  components: {
    ActionPopOver,
    CustomWarningModal,
    CreateAccessTokenModal,
    FeatureCard,
    CapabilitiesCard,
    AppApikeysCard,
  },

  setup() {
    const { copyToClipboard } = useClipboard();
    return { copyToClipboard };
  },

  data() {
    return {
      createOutline: 'edit-line',
      trashOutline: 'delete-line',
      reloadOutline: 'refresh-line',
      arrowBackOutline: 'arrow-left-line',
      isEditing: false,
      showDeleteAppModal: false,
      showDeleteKeyModal: false,
      keyToDelete: null,
      keyToRefresh: null,
      loading: false,
      featureLoading: false,
      keysLoading: false,
      showTokenModal: false,
      showClientSecret: false,
      clientSecrent: "",
      appNameError: "",
      appDescriptionError: "",
      capabilitiesError: "",
      appDetails: {
        app_name: "",
        app_id: "",
        description: "",
        date_created: "",
        created_by: "",
        status: "",
        api_products: [], // added to hold API products from app details
      },
      originalAppDetails: null,
      products: [], // full list of available products (capabilities)
      clientColumns: [
        { label: "Client ID", field: "client_id" },
        { label: "Client Secret", field: "client_secret" },
        { label: "Client Secret Status", field: "status" },
        { label: "Date Created", field: "created_at" },
        { label: "Last modified", field: "updated_at" },
        { label: "Access Token expires at", field: "access_token_expiry_at" },
        { label: "Access Token expires in", field: "access_token_expiry_seconds" },
        { label: "Actions", field: "actions" },
      ],
      clientData: [],
      loadingKeyId: null,
      showRevokeAppModal: false,
      showRevokeApiKeyModal: false,
      selectedApiKeyData: {
        id: null,
        status: "",
      },
      tokenGenerationMode: "access_token",
    };
  },

  async ionViewWillEnter() {
    useUIStore().updateLoading({ isLoading: true, message: 'Loading application details...' });
    try {
      await this.fetchAppDetails();
      this.fetchProducts();
      this.fetchApiKeys();
    } catch (error) {
      useUIStore().updateToast({
        open: true,
        message: error.message || "Something went wrong while loading data!",
        type: "danger",
      });
    } finally {
      useUIStore().updateLoading({ isLoading: false });
    }
  },

  methods: {
    getChipStyle,
    dateFormatter,
    handleCapabilityToggle({ index, enabled }) {
      this.products[index].enabled = enabled;

      if (this.capabilitiesError && this.products.some((p) => p.enabled)) {
        this.capabilitiesError = "";
      }
    },
    validateForm() {
      // Reset previous errors
      this.appNameError = "";
      this.appDescriptionError = "";
      this.capabilitiesError = "";

      //  Capabilities Validation
      const selected = this.products.filter((c) => c.enabled);

      let isValid = true;

      // App Name validation
      if (!this.appDetails.app_name.trim()) {
        const message = "Application Name is required.";
        this.appNameError = message;
        useUIStore().updateToast({ open: true, message, type: "danger" });
        isValid = false;
      } else if (this.appDetails.app_name.length > 30) {
        const message = "Application Name cannot exceed 30 characters.";
        this.appNameError = message;
        useUIStore().updateToast({ open: true, message, type: "danger" });
        isValid = false;
      } else if (this.appDetails.app_name.length < 4) {
        const message = "Application Name must be at least 4 characters long.";
        this.appNameError = message;
        useUIStore().updateToast({ open: true, message, type: "danger" });
        isValid = false;
      } else if (selected.length === 0) {
        const message = "Select at least one capability.";
        this.capabilitiesError = message;
        useUIStore().updateToast({ open: true, message, type: "danger" });
        isValid = false;
      }

      // Description validation
      if (this.appDetails.description.length > 100) {
        const message = "Application Description cannot exceed 100 characters.";
        this.appDescriptionError = message;
        useUIStore().updateToast({ open: true, message, type: "danger" });
        isValid = false;
      }

      return isValid;
    },
    openEditMode() {
      this.isEditing = true;
    },
    closeEditMode() {
      this.isEditing = false;
      this.appDetails = JSON.parse(JSON.stringify(this.originalAppDetails));
      this.mapEnabledProducts();
      this.appDescriptionError = "";
      this.appNameError = "";
      this.capabilitiesError = "";
    },
    // Revoke/Activate modal

    openRevokeAppModal() {
      this.showRevokeAppModal = true;
    },
    closeRevokeAppModal() {
      this.showRevokeAppModal = false;
    },
    openRevokeApiKeyModal(id, status) {
      this.showRevokeApiKeyModal = true;
      this.selectedApiKeyData.id = id;
      this.selectedApiKeyData.status = status;
    },
    closeRevokeApiKeyModal() {
      this.showRevokeApiKeyModal = false;
      this.selectedApiKeyData = {
        id: null,
        status: "",
      };
    },

    // App delete modal
    openDeleteAppModal() {
      this.showDeleteAppModal = true;
    },
    closeDeleteAppModal() {
      this.showDeleteAppModal = false;
    },

    // API key delete modal
    openDeleteKeyModal(keyId) {
      this.keyToDelete = keyId;
      this.showDeleteKeyModal = true;
    },
    closeDeleteKeyModal() {
      this.showDeleteKeyModal = false;
      this.keyToDelete = null;
    },
    // Access Token Modal
    openAccessTokenModal(row) {
      this.showTokenModal = true;
      this.keyToRefresh = row.id;
      this.tokenGenerationMode = row.access_token_expiry_at ? "refresh_token" : "access_token";
    },
    closeAccessTokenModal() {
      this.showTokenModal = false;
      this.keyToRefresh = null;
      this.tokenGenerationMode="access_token";
    },
    openClientSecretModal() {
      this.showClientSecret = true;
    },
    closeClientSecretModal() {
      this.showClientSecret = false;
      this.clientSecrent = "";
    },
    //  App methods
    async confirmDeleteApp() {
      this.closeDeleteAppModal();
      useUIStore().updateLoading({ isLoading: true, message: 'Deleting application...' });
      try {
        await deleteAppHelper({
          appId: this.$route.params.id,
          environment: environment,
          onSuccess: () => {
            this.closeDeleteAppModal();
            useUIStore().updateToast({
              open: true,
              message: "App deleted successfully!",
              type: "success",
            });
            this.$router.push("/developer-portal/applications");
          },
          onError: (error) => {
            useUIStore().updateToast({
              open: true,
              message: error.message || "Something went wrong!",
              type: "danger",
            });
          },
        });
      } finally {
        useUIStore().updateLoading({ isLoading: false });
      }
    },

    async fetchAppDetails() {
      try {
        this.loading = true;
        const id = this.$route.params.id;

        const app = await apiInterceptor.get(
          environment("VUE_APP_APIGEE_API_URL") + "apps/" + id
        );

        this.appDetails = {
          app_name: app.app_name,
          app_id: app.app_uuid,
          description: app.app_description,
          date_created: app.created_at,
          created_by: app.created_by,
          status: app.status,
          api_products: app.api_products || [],
        };
        this.originalAppDetails = JSON.parse(JSON.stringify(this.appDetails));
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message: error.response?.data?.detail || "Something went wrong!",
          type: "danger",
        });
      } finally {
        this.loading = false;
      }
    },

    async updateAppDetails() {
      if (!this.validateForm()) {
        return; // stop submit if invalid
      }
      try {
       useUIStore().updateLoading({ isLoading: true, message: 'Updating application...' });
        this.isEditing = false;
        // Build the payload
        const payload = {
          app_name: this.appDetails.app_name,
          app_description: this.appDetails.description,
          api_products: this.products
            .filter((product) => product.enabled) // only selected ones
            .map((product) => product.id),
        };
        const appId = this.$route.params.id;
        await apiInterceptor.put(
          environment("VUE_APP_APIGEE_API_URL") + `apps/${appId}`,
          payload
        );
        useUIStore().updateToast({
          open: true,
          message: "Application updated successfully!",
          type: "success",
        });
        await this.fetchAppDetails();
        
      } catch (error) {
        console.log('error', error);
        useUIStore().updateToast({
          open: true,
          message: error.response?.data?.detail || "Something went wrong while updating app!",
          type: "danger",
        });
        this.isEditing = true;
      } finally {
          useUIStore().updateLoading({ isLoading: false });
      }
    },

    async updateAppStatus() {
      this.closeRevokeAppModal(); // close before loading so overlay isn't behind dialog
      useUIStore().updateLoading({ isLoading: true, message: 'Updating application status...' });
      const currentStatus = this.appDetails.status?.toLowerCase();
      const newStatus = currentStatus === "active" ? "revoke" : "active";
      try {
        await updateAppStatusHelper({
          appId: this.$route.params.id,
          environment: environment,
          action: newStatus,
          onSuccess: async () => {
            useUIStore().updateToast({
              open: true,
              message: `App ${
                newStatus === "active" ? "activated" : "revoked"
              } successfully!`,
              type: "success",
            });
            await this.fetchAppDetails();
            await this.fetchApiKeys();
          },
          onError: (error) => {
            useUIStore().updateToast({
              open: true,
              message: error.message || "Something went wrong!",
              type: "danger",
            });
          },
        });
      } finally {
        useUIStore().updateLoading({ isLoading: false });
      }
    },

    async fetchApiKeys() {
      try {
        this.keysLoading = true;
        const id = this.$route.params.id;
        const keysResponse = await apiInterceptor.get(
          environment("VUE_APP_APIGEE_API_URL") + "apps/" + id + "/keys"
        );
        // The API uses 'key_id' (integer) as the path identifier for all key operations.
        // Map it to 'id' so row.id works throughout the component (delete, revoke, token, secret).
        this.clientData = (keysResponse.keys || []).map(key => ({
          ...key,
          id: key.key_id ?? key.id,
        }));
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message: error.response?.data?.detail || "Something went wrong!",
          type: "danger",
        });
      } finally {
        this.keysLoading = false;
      }
    },

    async addApiKey() {
      try {
        this.keysLoading = true;
        const appId = this.$route.params.id;
        await apiInterceptor.post(
          environment("VUE_APP_APIGEE_API_URL") + `apps/${appId}/keys`
        );
        useUIStore().updateToast({
          open: true,
          message: `API Key added successfully!`,
          type: "success",
        });
        await this.fetchApiKeys();
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message:
            error.response?.data?.detail || "Something went wrong while adding api key!",
          type: "danger",
        });
      } finally {
        this.keysLoading = false;
      }
    },

    async updateApiKeyStatus() {
      // Capture before closeRevokeApiKeyModal clears selectedApiKeyData
      const keyId = this.selectedApiKeyData.id;
      const newAction = this.selectedApiKeyData.status?.toLowerCase() === "active" ? "revoke" : "active";
      this.closeRevokeApiKeyModal(); // close before loading so overlay isn't behind dialog
      try {
        useUIStore().updateLoading({ isLoading: true, message: 'Updating API key status...' });
        const appId = this.$route.params.id;

        await apiInterceptor.post(
          environment("VUE_APP_APIGEE_API_URL") +
            `apps/${appId}/keys/${keyId}/status?action=${newAction}`
        );
        useUIStore().updateToast({
          open: true,
          message: `API Key ${
            newAction === "revoke" ? "revoked" : "activated"
          } successfully!`,
          type: "success",
        });
        await this.fetchApiKeys();
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message:
            error.response?.data?.detail || "Something went wrong while updating key status!",
          type: "danger",
        });
      } finally {
        useUIStore().updateLoading({ isLoading: false });
      }
    },

    async confirmDeleteApiKey() {
      if (!this.keyToDelete) return;
      const keyId = this.keyToDelete; // capture before closeDeleteKeyModal nulls it
      this.closeDeleteKeyModal(); // close first so loading overlay isn't behind dialog
      try {
        useUIStore().updateLoading({ isLoading: true, message: 'Deleting API key...' });
        const appId = this.$route.params.id;

        await apiInterceptor.delete(
          environment("VUE_APP_APIGEE_API_URL") +
            `apps/${appId}/keys/${keyId}`
        );
        useUIStore().updateToast({
          open: true,
          message: `API Key Deleted successfully!`,
          type: "success",
        });
        await this.fetchApiKeys();
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message:
            error.response?.data?.detail || "Something went wrong while deleting api key!",
          type: "danger",
        });
      } finally {  
        useUIStore().updateLoading({ isLoading: false });
      }
    },

    async fetchProducts() {
      try {
        this.featureLoading = true;
        const productsList = await apiInterceptor.get(
          environment("VUE_APP_APIGEE_API_URL") + "metadata/products"
        );

        this.products = productsList.products.filter((product)=>product.is_selectable).map((product) => ({
          id: product.id,
          name: product.name,
          description: product.description,
          enabled: false,
        }));
        this.mapEnabledProducts();
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message: error.response?.data?.detail || "Something went wrong!",
          type: "danger",
        });
      } finally {
        this.featureLoading = false;
      }
    },
    mapEnabledProducts() {
      if (!this.products.length || !this.appDetails.api_products.length) return;

      const enabledIds = this.appDetails.api_products.map((p) => p.id);

      this.products = this.products.map((prod) => ({
        ...prod,
        enabled: enabledIds.includes(prod.id),
      }));
    },

    async fetchClientSecretById(keyId, field) {
      try {
        this.loadingKeyId = keyId;

        const response = await apiInterceptor.get(
          environment("VUE_APP_APIGEE_API_URL") +
            `apps/${this.$route.params.id}/keys/${keyId}/secret`
        );

        this.clientSecrent = response[field];
        this.openClientSecretModal();
      } catch (err) {
        useUIStore().updateToast({
          open: true,
          message: err.response.data.detail || "Something went wrong!",
          type: "danger",
        });
      } finally {
        this.loadingKeyId = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.chat-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0px;
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--white);
  border-bottom: 1px solid var(--background-primary);
}
.toolbar-start {
  display: flex;
  align-items: center;
  gap: 10px;
}
.toolbar-end {
  display: flex;
  gap: 10px;
  padding-right: 15px;
}
.vivid-input {
  background: var(--background-primary);
  border-radius: 5px;
  min-height: 40px;
  width: 100%;
}
.vivid-input--readonly {
  background: var(--gray-01);
}
.vivid-textarea {
  background: var(--background-primary);
  border-radius: 5px;
  width: 100%;
}
.status-chip {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
}
.action-list {
  display: flex;
  flex-direction: column;
}
.action-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 15px;
  cursor: pointer;
  font-size: 14px;
  &:hover {
    background: var(--purple-opac);
    color: var(--purple);
    font-weight: 700;
  }
  &.disabled {
    opacity: 0.4;
    pointer-events: none;
  }
}
.heading-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.vivid-input--readonly {
  background: var(--gray-01);
}

.input-row {
  display: flex;
  flex-direction: row;
  gap: 20px;
  margin-bottom: 16px;
}

.sctn-input-readOnly {
  background-color: #f8f8f8;
  border-radius: 6px;
  padding: 8px 12px;
  color: #333;
  --padding-start: 12px;
}

.marginRight20 {
  margin-right: 20px;
}

.uuid-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
}

.card-spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 180px;
}

.heading-border {
  border-bottom: 1px solid var(--ion-color-light-shade);
  padding: 20px;
}
.application-input {
  margin: 0 !important;
  padding-bottom: 0px !important;
}
.editable-border {
  border: 1px solid var(--ion-color-primary) !important;
}
.error-text {
  color: red;
  margin-top: 4px;
  font-size: 13px;
}
.card-wrapper {
  border-radius: 8px;
}

.action-list .action-item:hover {
  background: var(--purple-opac);
  color: var(--purple);
  cursor: pointer;
  font-weight: 700;
}

@media (max-width: 768px) {
  .feature-card-wrapper {
    flex: 1 1 100%;
  }
  .input-wrapper {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  .application-input-wrapper {
    width: 100%;
  }
  .input-row {
    flex-direction: column;
  }
}
</style>
